import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { useElementPosition } from 'commons/customHooks/useElementPosition'
import { formatDate } from 'commons/utils/date'
import { simpleId } from 'commons/utils/format'
import { base64toFile } from 'commons/utils/object'
import { trim } from 'commons/utils/string'
import {
  Icon,
  Input,
  Label,
  type OptionProps,
  SectionCard,
  Select,
  SlimButton,
  UploadButton,
} from 'components'
import { OVTypes } from 'constants/enums'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { statusNegDiffApproval } from 'services/nd/constants'
import { getDetailedInfo, saveOvFields } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export const NDSection = () => {
  const { ovNumber } = useParams()
  const dispatch = useDispatch()

  const {
    ovSelected: {
      Cabecalho: { CodigoStatus, CodigoTipoOrdem },
    },
    detailedInfo: { Tracking },
    observation,
    loadingObservation: loading,
    reasonsJustification,
    NDJustificationDescription,
  } = useSelector((state) => state.ov)

  useEffect(() => {
    if (!Tracking) {
      dispatch(getDetailedInfo(ovNumber ?? ''))
    }
  }, [ovNumber])

  const yrbaCode = '12' // TODO: Create an ENUM for this
  const isYRBA = useMemo(() => CodigoTipoOrdem === OVTypes.YRBA, [CodigoTipoOrdem])

  const defaultValues = {
    codReason: isYRBA ? yrbaCode : (observation?.CodigoMotivoNegociacaoDiferenciada ?? ''),
    descReason: observation?.DescricaoMotivoNegociacaoDiferenciada ?? '',
    file: observation?.Arquivo
      ? base64toFile(observation?.Arquivo, observation?.NomeArquivo ?? '')
      : new File([], ''),
    file_name: observation?.NomeArquivo ?? '',
    requestDescription: observation?.Justificativa ?? '',
  }

  const {
    control,
    setValue,
    getValues,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues,
  })

  const codReason = watch('codReason')
  const requestDescription = watch('requestDescription')
  const isFormValid = trim(codReason) !== '' && trim(requestDescription) !== ''

  const [keyFile, setKeyFile] = useState(simpleId())

  // TODO: Refactor this, is basicly a copy of the useEffect below
  useEffect(() => {
    if (isYRBA) {
      const yrbaReason = reasonsOption().find((item) => item.value === yrbaCode)

      // TODO: Refactor this, yrbaReason?.title must be required
      reset({
        ...defaultValues,
        codReason: yrbaCode,
        descReason: yrbaReason?.title as string,
        requestDescription: NDJustificationDescription,
      })
    }
  }, [NDJustificationDescription])

  // TODO: Refactor this, is basicly a copy of the useEffect above
  useEffect(() => {
    if (isYRBA) {
      // TODO: Refactor this, yrbaReason?.title must be required
      const yrbaReason = reasonsOption().find((item) => item.value === yrbaCode)
      setValue('codReason', yrbaCode)
      setValue('descReason', yrbaReason?.title as string)
    }
  }, [CodigoTipoOrdem])

  useEffect(() => {
    if (observation?.Arquivo && observation?.NomeArquivo) {
      reset(defaultValues)
    }
  }, [observation?.Arquivo])

  const flagNd = observation.StatusNegociacaoDiferenciadaAprovacao
  const motivo = observation.MotivoReprovacao

  const selectRef = useRef<HTMLDivElement>(null)
  const reasonsMenuPosition = useElementPosition(selectRef)

  const reasonsOption = (): OptionProps[] =>
    reasonsJustification &&
    reasonsJustification
      .filter((item) => {
        if (!isYRBA) {
          return (
            item.CodigoMotivoNegociacao !== '15' && item.CodigoMotivoNegociacao !== '12' // TODO: Refactor to remove magic numbers
          )
        }
        return true
      })
      .map((reason) => ({
        title: reason.DescricaoMotivoNegociacao,
        value: reason.CodigoMotivoNegociacao,
      }))

  const returnNDStatus = (): React.ReactElement | null => {
    const statusNd: Record<string, React.ReactElement> = {
      [statusNegDiffApproval.backOffice]: (
        <>
          <Icon color={theme.colors.neutral.mediumhover} icon="clock" size="18" />
          <span>Pendente</span>
        </>
      ),
      [statusNegDiffApproval.directors]: (
        <>
          <Icon color={theme.colors.alert.success.dark} icon="task-alt" size="18" />
          <span>Aprovada</span>
        </>
      ),
      [statusNegDiffApproval.reject]: (
        <>
          <Icon color={theme.colors.alert.danger.darker} icon="report" size="18" />
          <span>Reprovada</span>
        </>
      ),
      [statusNegDiffApproval.pending]: (
        <>
          <Icon color={theme.colors.neutral.mediumhover} icon="clock" size="18" />
          <span>Pendente</span>
        </>
      ),
    }

    return flagNd && statusNd[flagNd]
  }

  const handleSaveOV = () => {
    if (isFormValid) {
      const data = getValues()

      dispatch(
        saveOvFields(
          data.file,
          data.file_name,
          data.descReason,
          data.codReason,
          data.requestDescription,
        ),
      )

      reset({ ...data })
    }
  }
  return (
    <S.ColumnWrapper>
      <SectionCard
        title="Negociação diferenciada"
        tooltipText="Esta é uma seção obrigatória exibida a partir do registro de desconto, condição de pagamento ou prazo extra superior ao permitido pelas condições comerciais do cliente vinculado a esta OV. Além de acessar a justificativa da OV,  você também pode acompanhar o status de análise e, caso reprovada, o motivo da reprovação da ND."
      >
        <S.Wrapper>
          <S.Form>
            {(flagNd === statusNegDiffApproval.reject ||
              flagNd === statusNegDiffApproval.directors) && (
              <div className="row no-gap">
                <div className="infoRow2 firstColumn">
                  <div className="infoColumn">
                    <Label>Status</Label>
                    <div className="info">{returnNDStatus()}</div>
                  </div>
                  <div className="infoColumn">
                    <Label>Última atualização</Label>
                    <div className="info">{formatDate(Tracking?.DataUltimaAlteracao)}</div>
                  </div>
                </div>
                {flagNd === 'R' && ( // TODO: Create an ENUM for this
                  <div className="infoRow expand">
                    <div className="infoColumn">
                      <Label>Motivo de reprovação</Label>
                      <div className="info">{motivo}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="row">
              <S.FormField className="firstColumn">
                <div className="selectContainer" ref={selectRef}>
                  <Controller
                    control={control}
                    defaultValue={observation?.CodigoMotivoNegociacaoDiferenciada ?? ''}
                    name={'codReason'}
                    render={({ field: { name, onChange, value } }) => (
                      <Select
                        disabled={
                          loading || +CodigoStatus !== 0 || !reasonsJustification.length || isYRBA
                        }
                        label="Motivo da solicitação"
                        name={name}
                        onChange={(e) => {
                          const reason = reasonsOption().find((r) => r.value === e.target.value)
                          reason && setValue('descReason', reason?.title)

                          onChange(e.target.value)
                        }}
                        options={reasonsOption()}
                        optionsMaxHeight={300}
                        optionsMaxWidth={reasonsMenuPosition}
                        required
                        value={value}
                      />
                    )}
                  />
                </div>
              </S.FormField>
              <S.FormField className="firstColumn">
                <div className="description">
                  <Controller
                    control={control}
                    defaultValue={observation?.Justificativa ?? ''}
                    name="requestDescription"
                    render={({ field: { onChange, value } }) => (
                      <Input
                        disabled={loading || +CodigoStatus !== 0}
                        height={132}
                        InputProps={{
                          placeholder: 'Digite aqui mais informações sobre a solicitação',
                        }}
                        label="Descrição da solicitação"
                        maxLength={264}
                        onChange={onChange}
                        required
                        rows={5}
                        showMaxLength
                        value={value}
                      />
                    )}
                  />
                </div>
              </S.FormField>
              <S.FormField className="firstColumn">
                <div>
                  <Controller
                    control={control}
                    name="file"
                    render={({ field: { onChange, value } }) => (
                      <UploadButton
                        disabled={loading || +CodigoStatus !== 0}
                        id="fileInput"
                        key={`fileInput-${keyFile}`}
                        name="upload"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.files?.length) {
                            onChange(e.target.files[0])
                            setValue(`file_name`, e.target.files[0].name, {
                              shouldDirty: true,
                            })
                          }
                        }}
                        onClear={() => {
                          setKeyFile(simpleId())
                          setValue('file', new File([], ''))
                          setValue('file_name', '', { shouldDirty: true })
                        }}
                        placeholder="Clique aqui para selecionar arquivo"
                        required={isYRBA}
                        value={value as File}
                      />
                    )}
                  />
                </div>
              </S.FormField>
            </div>
            {isDirty && (
              <S.FormActions>
                <SlimButton
                  content="Salvar"
                  disabled={loading || !isFormValid}
                  loading={loading}
                  onClick={() => handleSaveOV()}
                  type="button"
                  variant="primary"
                />
                <SlimButton
                  content="Desfazer"
                  disabled={loading}
                  onClick={() => reset(defaultValues)}
                  type="button"
                  variant="secondary"
                />
              </S.FormActions>
            )}
          </S.Form>
        </S.Wrapper>
      </SectionCard>
    </S.ColumnWrapper>
  )
}
